import { TranscriptionWord } from '../types';

// Edit operation types for word-level diff
export type EditOperation = 'match' | 'substitution' | 'insertion' | 'omission';

export interface WordDiff {
  operation: EditOperation;
  targetWord: string | null;      // The expected word (null for insertions)
  transcribedWord: string | null; // The spoken word (null for omissions)
  confidence: number;             // Confidence score (0 for omissions)
  targetIndex: number | null;     // Index in target array
  transcribedIndex: number | null; // Index in transcribed array
}

export interface LevenshteinResult {
  distance: number;               // Total edit distance
  diffs: WordDiff[];              // Detailed word-by-word diff
  stats: {
    matches: number;
    substitutions: number;
    insertions: number;
    omissions: number;
  };
}

/**
 * Normalize a word for comparison (lowercase, remove punctuation)
 */
export const normalize = (word: string): string => {
  return word.toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Check if a word is meaningful (not just punctuation)
 */
const isMeaningfulWord = (word: string): boolean => {
  return normalize(word).length > 0;
};

// Helper type for tracking original indices
interface IndexedWord {
  word: string;
  originalIndex: number;
}

interface IndexedTranscribedWord {
  word: TranscriptionWord;
  originalIndex: number;
}

/**
 * Calculate word-level Levenshtein distance with backtracking to get the edit operations.
 * Uses dynamic programming to find the minimum edit distance and then backtracks
 * to determine which operations (match, substitution, insertion, omission) were performed.
 *
 * Punctuation-only tokens are filtered out before comparison.
 */
export function calculateLevenshteinDistance(
  targetWords: string[],
  transcribedWords: TranscriptionWord[]
): LevenshteinResult {
  // Filter out punctuation-only words while tracking original indices
  const filteredTarget: IndexedWord[] = targetWords
    .map((word, index) => ({ word, originalIndex: index }))
    .filter(item => isMeaningfulWord(item.word));

  const filteredTranscribed: IndexedTranscribedWord[] = transcribedWords
    .map((word, index) => ({ word, originalIndex: index }))
    .filter(item => isMeaningfulWord(item.word.text));

  const m = filteredTarget.length;
  const n = filteredTranscribed.length;

  // Create DP matrix
  // dp[i][j] = minimum edit distance for targetWords[0..i-1] and transcribedWords[0..j-1]
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  // Initialize base cases
  for (let i = 0; i <= m; i++) {
    dp[i][0] = i; // Deleting all target words (omissions)
  }
  for (let j = 0; j <= n; j++) {
    dp[0][j] = j; // All transcribed words are insertions
  }

  // Fill the DP matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const targetNorm = normalize(filteredTarget[i - 1].word);
      const transcribedNorm = normalize(filteredTranscribed[j - 1].word.text);

      if (targetNorm === transcribedNorm) {
        // Words match
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],     // Omission (target word was skipped)
          dp[i][j - 1],     // Insertion (extra word was spoken)
          dp[i - 1][j - 1]  // Substitution (word was replaced)
        );
      }
    }
  }

  // Backtrack to find the edit operations
  const diffs: WordDiff[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      const targetItem = filteredTarget[i - 1];
      const transcribedItem = filteredTranscribed[j - 1];
      const targetNorm = normalize(targetItem.word);
      const transcribedNorm = normalize(transcribedItem.word.text);

      if (targetNorm === transcribedNorm) {
        // Match
        diffs.unshift({
          operation: 'match',
          targetWord: targetItem.word,
          transcribedWord: transcribedItem.word.text,
          confidence: transcribedItem.word.confidence,
          targetIndex: targetItem.originalIndex,
          transcribedIndex: transcribedItem.originalIndex
        });
        i--;
        j--;
      } else if (dp[i][j] === dp[i - 1][j - 1] + 1) {
        // Substitution
        diffs.unshift({
          operation: 'substitution',
          targetWord: targetItem.word,
          transcribedWord: transcribedItem.word.text,
          confidence: transcribedItem.word.confidence,
          targetIndex: targetItem.originalIndex,
          transcribedIndex: transcribedItem.originalIndex
        });
        i--;
        j--;
      } else if (dp[i][j] === dp[i - 1][j] + 1) {
        // Omission (target word was skipped)
        diffs.unshift({
          operation: 'omission',
          targetWord: targetItem.word,
          transcribedWord: null,
          confidence: 0,
          targetIndex: targetItem.originalIndex,
          transcribedIndex: null
        });
        i--;
      } else {
        // Insertion (extra word was spoken)
        diffs.unshift({
          operation: 'insertion',
          targetWord: null,
          transcribedWord: transcribedItem.word.text,
          confidence: transcribedItem.word.confidence,
          targetIndex: null,
          transcribedIndex: transcribedItem.originalIndex
        });
        j--;
      }
    } else if (i > 0) {
      // Remaining target words are omissions
      const targetItem = filteredTarget[i - 1];
      diffs.unshift({
        operation: 'omission',
        targetWord: targetItem.word,
        transcribedWord: null,
        confidence: 0,
        targetIndex: targetItem.originalIndex,
        transcribedIndex: null
      });
      i--;
    } else {
      // Remaining transcribed words are insertions
      const transcribedItem = filteredTranscribed[j - 1];
      diffs.unshift({
        operation: 'insertion',
        targetWord: null,
        transcribedWord: transcribedItem.word.text,
        confidence: transcribedItem.word.confidence,
        targetIndex: null,
        transcribedIndex: transcribedItem.originalIndex
      });
      j--;
    }
  }

  // Calculate stats
  const stats = {
    matches: diffs.filter(d => d.operation === 'match').length,
    substitutions: diffs.filter(d => d.operation === 'substitution').length,
    insertions: diffs.filter(d => d.operation === 'insertion').length,
    omissions: diffs.filter(d => d.operation === 'omission').length
  };

  return {
    distance: dp[m][n],
    diffs,
    stats
  };
}

/**
 * Determine if a match should be considered "unclear" based on confidence threshold
 */
export function getMatchQuality(confidence: number, threshold: number = 0.8): 'correct' | 'unclear' {
  return confidence > threshold ? 'correct' : 'unclear';
}
